/**
 * Whether a conversation-id change drops the composer DRAFT — the typed text, the staged attachments
 * and the contact chips, i.e. exactly the set `send` clears together.
 *
 * A draft belongs to the thread it was typed in. Carrying it into a DIFFERENT conversation is how a
 * half-written message — or, worse, a staged upload — ends up in the wrong chat: on this surface the
 * draft is `MessagesPanel` state and the panel stays mounted across a conversation switch (the docked
 * chat surface is never unmounted), so the state simply survives into the other thread.
 *
 * Leaving the chat surface is deliberately NOT what clears it. Opening another page to check a name
 * and coming back must not cost the draft, which is why this reads the CONVERSATION, not the route.
 *
 * The empty-`previous` guard is the fresh-chat case: a brand-new chat has no id until its first send,
 * so `null → <real id>` is that chat being PROMOTED, not a switch, and clearing there would eat a
 * follow-up the user is already typing while the id resolves. `<real id> → null` (starting a new chat)
 * does clear.
 *
 * `null` and `''` both mean "no id yet", so the case table matches the native twins, which use `''`:
 * iOS `ChatDraftReset.clearsDraft` and Android `clearsDraftOnConversationChange`.
 */
export function clearsDraftOnConversationChange(
  previous: string | null | undefined,
  next: string | null | undefined,
): boolean {
  return !!previous && (previous ?? '') !== (next ?? '');
}
